/**
 * Pure presentation helpers for the Context + AI pane (pane C) —
 * docs/design-handoff/tossker-redesign/README.md §5 "Context pane" (task 5,
 * Tossker repaint) supersedes the older docs/design-handoff/README.md "4.
 * Context + AI pane (pane C)" this file originally followed; both are cited
 * below by section. Nothing here fetches anything; every function turns data
 * the pane was already handed (from an ops read, never the store) into copy
 * and token classes. See ../../lib/ops.ts and ./aiOps.ts for the reads.
 *
 * Literal copy is taken verbatim from whichever README gave an exact string.
 * Where `ThreadDetail` (packages/shared/src/types.ts) has no field to back a
 * value a prototype fabricated for its demo tickets (a subject line, a
 * contact's job-title "role", a per-leg ring duration on `CallFacts`), this
 * module renders an honest blank — or omits the row entirely — instead of
 * inventing one. See task-16-report.md "Decisions" and this task's own
 * report for the "THIS CALL" block's omitted "Rang for" row (no `ringS`
 * field exists on `CallFacts`, only per-leg in `CallLeg`, which this pane
 * does not fetch).
 */
import type { CallState, Channel } from '@communicator/shared'
import { formatHmInZone } from '@communicator/shared'
import { CHANNEL_LABEL } from '../rail/format'

/**
 * Task 5 (Tossker repaint), README §5: "Ticket id in mono 14px, 'Linked
 * manually · subject loads from Halo'." Supersedes the older prototype's
 * "linked manually · details load from Halo" (same fact — Halo is the
 * record, never a fabricated subject — restated in the new design's own
 * wording).
 */
export const TICKET_MANUAL_META = 'Linked manually · subject loads from Halo'

/** Verbatim from the README and the prototype's dashed no-ticket card (line 399). */
export const NO_TICKET_TEXT = 'No ticket attached. Nothing about this thread is written to Halo.'

export const AI_SEAM_TITLE = 'NOT WIRED · SEAM ONLY'

/** Shown when the route answered but could not help — the reason sits under it. */
export const AI_UNAVAILABLE_TITLE = 'NO SUGGESTIONS RIGHT NOW'

/**
 * The Halo priority names, so a chip here reads like the ticket a tech is
 * about to raise rather than a second vocabulary they have to translate.
 */
export const PRIORITY_LABEL: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: 'Critical',
  2: 'High',
  3: 'Medium',
  4: 'Low',
  5: 'Next Time',
}

/**
 * Red through blue, matching the same scale.
 *
 * Design tokens, not hex: the repo has a guard test forbidding colour literals
 * outside tokens.css, and it caught this. Four of the five already existed as
 * status colours meaning exactly this — only Medium's yellow was new.
 */
export const PRIORITY_CLASS: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: 'bg-bad text-black',
  2: 'bg-warn text-black',
  3: 'bg-caution text-black',
  4: 'bg-ok text-black',
  5: 'bg-info text-black',
}
export const AI_SEAM_BLURB =
  'Pane C reads the thread and returns suggestions and a sentiment read. The thread is already the primary object, so this is additive.'

/** Verbatim from the prototype's PRIORITY READ note (line 444) and README "Priority and sort" — a
 * true statement about the sort mechanism itself, not a per-thread AI read, so it is safe to show
 * as static copy even while the AI tab is otherwise unwired. */
export const PRIORITY_READ_NOTE = 'Adds a bump on top of the band sort. Never folded into another factor.'

/**
 * Visual findings item 3: this pane used to own its own ALL-CAPS tier
 * vocabulary here (`TIER_LABEL` / `TIER_VALUE_CLASS` — "VERIFIED" as a bare
 * `KvRow` value, no icon), independent of the thread header's tier badge
 * (`../thread/format.ts`'s `TIER_BADGE_ICON` / `TIER_BADGE_TEXT` /
 * `tierBadgeStyle` — title-case, `shield-check`/`user` icon, the
 * `--status-success-soft` / `--tint-primary` / dashed-gold pill treatments).
 * Two panes independently describing the same fact is exactly how they drifted
 * apart. `../thread/format.ts` is the one the thread header already ships
 * and already has coverage for, so it is the owner now — `ContextTab.tsx`
 * imports its three exports directly rather than this file re-declaring (or
 * re-exporting) a second copy.
 */

/**
 * Voice liveness is read from the call's own state, never the generic
 * `Liveness` field — `deriveLiveness` in services/ops/src/threads.ts folds
 * both `ended` and `missed` into the generic `over`, which cannot tell
 * "missed" from "call over" apart. The README's exact four voice words
 * ("ringing" / "live call" / "missed" / "call over") only exist at the
 * `CallState` level.
 */
export function contextLivenessLabel(channel: Channel, genericLiveness: string, callState: CallState | null): string {
  if (channel === 'voice') {
    switch (callState) {
      case 'ringing':
        return 'ringing'
      case 'live':
        return 'live call'
      case 'missed':
        return 'missed'
      case 'ended':
        return 'call over'
      default:
        break
    }
  }
  if (genericLiveness === 'online') return 'online'
  if (genericLiveness === 'live') return 'online, live'
  return 'async'
}

/**
 * README "10. Voice call flow": "the SLA row reads 'callback needed' or
 * 'disposition needed' instead of a response target" for a missed/ended
 * call. Ringing/live voice shows 'immediate' (prototype line 1718). Every
 * other channel derives its label from the thread's own `slaTargetS` —
 * never a hardcoded number.
 */
export function contextSlaLabel(channel: Channel, callState: CallState | null, slaTargetS: number): string {
  if (channel === 'voice') {
    if (callState === 'missed') return 'callback needed'
    if (callState === 'ended') return 'disposition needed'
    return 'immediate'
  }
  return slaLabelFromSeconds(slaTargetS)
}

export function slaLabelFromSeconds(slaTargetS: number): string {
  const s = Math.max(0, Math.round(slaTargetS))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.round(s / 60)} min`
  return `${Math.round(s / 3600)} hr`
}

/** README "10. Voice call flow": "a 'Voice source: 3CX Webhooks API · read-only' row". */
export function voiceSourceLabel(channel: Channel): string {
  return channel === 'voice' ? '3CX Webhooks API · read-only' : '—'
}

/**
 * Formats a US-shaped phone number for humans: `+13605550100`/`3605550100`
 * (any separators — parens, dashes, spaces, dots — are stripped and
 * ignored) become `+1 (360) 555-0100` / `(360) 555-0100`. Anything that
 * isn't a clean 10-digit or 11-digit-leading-1 number — an international
 * number, a short code, garbage — is handed back exactly as given: this
 * function's whole job is "make a US number read nicely," not "parse phone
 * numbers," and guessing at an unfamiliar shape risks mangling a real
 * international number into something that reads as a wrong US one.
 * `null`/`undefined`/empty input is `null` (no number to show), never the
 * empty string.
 */
export function formatPhoneNumber(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null
  const digits = value.replace(/\D/g, '')
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return value
}

/**
 * Design 2026-08-24 (client-version-visibility, extended
 * 2026-08-24-sms-number-in-context): the Channel row's " — <detail>"
 * annotation. Started chat-only (the client app's " — v<version>"); Andrew's
 * follow-up ask — "Can we see the phone number we're communicating with for
 * SMS the same way same place?" — extends the same row to sms, using the
 * counterparty number the thread already carries (`ThreadDetail.targetId`).
 * For sms (and voice) `targetId` holds that counterparty phone number; the
 * chat branch below deliberately never reads it — chat's own scope on that
 * field (contactId/email, or `device:<id>`) is not a version, so chat shows
 * `version` instead regardless of what `targetId` happens to hold.
 *
 * `'web'` is a real, deliberately-sent value on the wire (Andrew wants to
 * know "they're on the web client, not the app" as much as which shell
 * build), but it is not a version number — running it through the same
 * `v<version>` template as a real shell semver produced the nonsensical
 * "chat — vweb" for every browser client (guaranteed, not an edge case: the
 * literal is unconditional in `./version.ts`'s no-bridge fallback). So chat
 * distinguishes three renderings: a real version gets its "v" prefix, the
 * web literal gets none, and no version — unknown or not applicable — is
 * the plain channel label, never "unknown".
 *
 * sms shows the formatted `targetId` when present, else the bare label —
 * same "no version" fallback shape as chat, just keyed on a different field.
 *
 * Voice is deliberately untouched: its number already lives on the call
 * card's own "Source" row (`voiceSourceLabel` below), so this function never
 * even looks at `targetId` for that channel.
 */
export interface ChannelRowContext {
  /** Chat only — the contact's last-reported client app version, or `null`. */
  version: string | null
  /** Sms only — the counterparty phone number (`ThreadDetail.targetId`), or `null`. */
  targetId: string | null
}

export function channelRowLabel(channel: Channel, context: ChannelRowContext): string {
  const label = CHANNEL_LABEL[channel]
  if (channel === 'chat') {
    if (!context.version) return label
    return context.version === 'web' ? `${label} — web` : `${label} — v${context.version}`
  }
  if (channel === 'sms' && context.targetId) {
    const formatted = formatPhoneNumber(context.targetId)
    return formatted ? `${label} — ${formatted}` : label
  }
  return label
}

function formatHHMM(iso: string, timeZone: string): string {
  return formatHmInZone(iso, timeZone)
}

/** Never a literal ("11:00 · hourly rollup" was the prototype's fabricated demo value) — always the
 * thread's own `lastSyncedAt`, or an honest "not yet synced"/"never · no ticket". */
export function lastSyncedLabel(hasTicket: boolean, lastSyncedAt: string | null, timeZone: string): string {
  if (!hasTicket) return 'never · no ticket'
  if (!lastSyncedAt) return 'not yet synced'
  return formatHHMM(lastSyncedAt, timeZone)
}

/**
 * The held-sync chip. Resolution #4: the count is always the thread's real
 * `heldWrites`, never a placeholder — "N note(s) waiting to sync" only
 * renders when that count is truthy; the Halo-unreachable case is the only
 * other reason the chip shows at all.
 */
export function pendingSyncVisible(heldWrites: number, haloUp: boolean, hasTicket: boolean): boolean {
  return hasTicket && (heldWrites > 0 || !haloUp)
}

export function pendingSyncLabel(heldWrites: number): string {
  return heldWrites > 0 ? `${heldWrites} note(s) waiting to sync` : 'Held — Halo unreachable'
}

/* ------------------------------------------------------------------ *
 * THIS THREAD — task 5 (Tossker repaint), README §5: "waiting time as
 * '3m of 15m' plus a 5px `--surface-sunken` progress bar filled
 * `--teal-500`." Both derive from `waitS`/`slaTargetS`, which every
 * `ThreadSummary` (and therefore every `ThreadDetail`) always carries —
 * never a placeholder, never fetched separately.
 * ------------------------------------------------------------------ */

function minutesLabel(seconds: number): string {
  return `${Math.max(0, Math.round(seconds / 60))}m`
}

export function waitProgressLabel(waitS: number, slaTargetS: number): string {
  return `${minutesLabel(waitS)} of ${minutesLabel(slaTargetS)}`
}

/** Clamped 0..1 — a thread already past its SLA target still draws a full bar, never an overflowing one. */
export function waitProgressFraction(waitS: number, slaTargetS: number): number {
  if (slaTargetS <= 0) return 0
  return Math.max(0, Math.min(1, waitS / slaTargetS))
}

/* ------------------------------------------------------------------ *
 * THIS CALL — task 5, README §5: "Direction / Rang for / Talking /
 * Extension / Source, mono values, and 'No recording — this PBX does not
 * hand one over.'" Voice-only; `ContextTab` gates this block on
 * `detail.channel === 'voice' && detail.call !== null`.
 *
 * "Rang for" is deliberately NOT built: `CallFacts` (packages/shared/src/
 * types.ts) carries no ring-duration field of its own — only per-leg
 * `CallLeg.ringS`, which lives behind `ops.callLegs()` (a fetch
 * `CallSummary.tsx` makes for the finished-call journey, not something this
 * pane reads). Rather than fetch a second, unrelated dataset just to fill
 * one row, or fabricate a number, this block omits the row — the same
 * "invent no detail whatsoever" rule task-16-report.md already established
 * for this pane's IDENTITY block.
 *
 * A tiny local mm:ss formatter, duplicated from ../thread/format.ts's
 * `formatClock` rather than imported — same non-coupling `../rail/format.ts`
 * already established for its own `formatFloorClock` (see that file's doc
 * comment): pane C and pane B stay decoupled, and five lines is cheaper than
 * the cross-pane import.
 */
function formatCallClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

export const CALL_DIRECTION_LABEL: Record<'in' | 'out', string> = {
  in: 'Inbound',
  out: 'Outbound',
}

/** "In progress" while live (no final duration yet), else the recorded duration — never "0:00" for a call still going. */
export function callTalkingLabel(callState: CallState, durationS: number | null): string {
  if (callState === 'live') return 'In progress'
  if (durationS === null || durationS <= 0) return '—'
  return formatCallClock(durationS)
}

/** Verbatim, README §5. */
export const NO_RECORDING_TEXT = 'No recording — this PBX does not hand one over.'

/** Gated on the call's own fields, not assumed — if a recording ever does attach, this line must stop claiming there isn't one. */
export function hasNoRecording(call: { recordingUrl: string | null; recordingAttached: boolean }): boolean {
  return call.recordingUrl === null && !call.recordingAttached
}
